/**
 * Pre-signup preview → onboarding draft handoff.
 *
 * The preview builder at /magic-onboarding-v2 collects a business name, one
 * service, and a booking theme from anonymous visitors, then merges them into
 * the canonical `businessSignupData` draft so the work survives account
 * creation. There is deliberately no parallel persistence model — this writes
 * the same draft the v2 onboarding flow reads.
 */
import { defaultFormData, v2Slugify } from '@/components/onboarding/onboardingDefaults';
import { DEFAULT_BOOKING_THEME_KEY, type V2Draft } from './v2OnboardingDraft';

/** Fallback handle shown in the preview before a business name is typed. */
export const PREVIEW_FALLBACK_HANDLE = 'your-page';
export const PREVIEW_SERVICE_PRICE = 45;
export const PREVIEW_SERVICE_DURATION = 45;

/** Handle rules, mirrored from useV2BookingUrlCheck so the preview can't produce a draft the Business step rejects. */
export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 40;
export const HANDLE_PATTERN = /^[a-z0-9-]+$/;

export interface PreviewInput {
  businessName: string;
  serviceName: string;
  themeKey: string;
}

/** Strip anything the booking URL can't contain, as the visitor types. */
export function normalizeHandle(raw: string): string {
  return (raw || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, HANDLE_MAX_LENGTH);
}

/** The handle proposed for a business name, matching what the preview displays. */
export function previewHandle(businessName: string): string {
  const proposed = v2Slugify(businessName.trim());
  if (!proposed) return PREVIEW_FALLBACK_HANDLE;
  return proposed.length >= HANDLE_MIN_LENGTH ? proposed : `${proposed}-book`;
}

/** Format-level validation. Returns null when the handle is well-formed. */
export function handleFormatError(handle: string): string | null {
  if (!handle) return null;
  if (!HANDLE_PATTERN.test(handle)) return 'Lowercase letters, numbers, and hyphens only.';
  if (handle.length < HANDLE_MIN_LENGTH) return `At least ${HANDLE_MIN_LENGTH} characters required.`;
  if (handle.length > HANDLE_MAX_LENGTH) return `Maximum ${HANDLE_MAX_LENGTH} characters.`;
  return null;
}

/** True when a draft was seeded by the preview builder. */
export function isPreviewDraft(draft: V2Draft | null): boolean {
  return draft?._previewOrigin === true;
}

/**
 * Merge preview input into an existing draft (or create one from defaults).
 *
 * Merge rules:
 *  - The booking handle is generated from the business name. The authenticated
 *    Business step still re-validates it and lets the user edit it if needed.
 *  - The preview service is prepended unless an existing service already has
 *    the same name, so a prior draft is preserved rather than clobbered.
 *  - `completedSteps` is reset: v2 derives its resume step from field presence,
 *    and carrying a v1 completion marker through would make `loadV2Draft`
 *    discard this draft on the next read.
 */
export function buildPreviewDraft(existing: V2Draft | null, input: PreviewInput): V2Draft {
  const businessName = input.businessName.trim();
  const serviceName = input.serviceName.trim();
  const handle = previewHandle(businessName);
  const existingServices = Array.isArray(existing?.services) ? existing.services : [];
  const hasMatchingService = existingServices.some(
    (service) => service.name.trim().toLowerCase() === serviceName.toLowerCase(),
  );
  const previewService = {
    id: `preview-${normalizeHandle(serviceName) || 'service'}`,
    name: serviceName,
    description: '',
    duration: PREVIEW_SERVICE_DURATION,
    price: PREVIEW_SERVICE_PRICE,
    bufferTime: 0,
    waitlistEnabled: false,
  };

  return {
    ...defaultFormData,
    ...existing,
    businessName,
    businessType: existing?.businessType || 'solo',
    bookingUrl: existing?.bookingUrl || handle,
    title: existing?.title || 'Book an appointment',
    services: serviceName && !hasMatchingService
      ? [previewService, ...existingServices]
      : existingServices,
    bookingFlowThemeKey: input.themeKey || DEFAULT_BOOKING_THEME_KEY,
    completedSteps: [],
    _savedAt: new Date().toISOString(),
    _previewOrigin: true,
  };
}
